/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import {
  CardSkeleton,
  MapSkeleton,
} from "@/dashboard/components/program/ProgramList";
import ProjectList from "@/dashboard/components/project/ProjectList";
import { useModal } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { useDatabase } from "@/programs/hooks/useDatabase";
import { FormEvent, lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { useNavigate, useParams } from "react-router-dom";
import AddProject from "./AddProject";

import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import useScopedDashboardAccess from "@/auth/hooks/useScopedDashboardAccess";
import { autoScrollJoyrideStep } from "@/libs/joyrideAutoScroll";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ChartSkeleton } from "@/dashboard/components/layout/ChartsDashboard";
import {
  PROGRAM_QUERY_GC_TIME,
  PROGRAM_QUERY_STALE_TIMES,
  programQueryKeys,
  removeProgramProjectFromProgramsInfoCache,
  updateProgramInProgramsInfoCache,
  upsertProgramProjectInProgramsInfoCache,
} from "@/dashboard/programQueryConfig";
import { useMutateData } from "@/hooks/useMutation";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Edit3,
  Eye,
  Filter,
  X,
} from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type FundSearcherFilter = "all" | "me" | "both";
type FinancingFilter = "all" | "full" | "partial" | "none";
type StatusFilter = "all" | 0 | 1 | 2 | 3 | 4 | 5 | 6;

const STATUS_LABELS: Record<number, string> = {
  0: "Non commencé",
  1: "En cours",
  2: "En attente de validation",
  3: "Validé",
  4: "Rejeté",
  5: "Cloturé",
  6: "En attente de votre validation",
};

const LazyTab = lazy(() => import("./TabProgram"));

const useDeferredActivation = (delayMs: number) => {
  const [active, setActive] = useState(() => typeof window === "undefined");

  useEffect(() => {
    if (typeof window === "undefined") return;

    let timerId: number | null = null;
    const frameId = window.requestAnimationFrame(() => {
      timerId = window.setTimeout(() => {
        setActive(true);
      }, delayMs);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, [delayMs]);

  return active;
};

const DashboardProgram = () => {
  const { setOpenModal, modals } = useModal();
  const { updateProgramInDB } = useDatabase();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const programsEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/info/programs`;
  const programInfoEndpoint = id
    ? `${import.meta.env.VITE_API_ENDPOINT}/info/programs?program=${id}`
    : "";
  const countriesEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/countries`;

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [programName, setProgramName] = useState("");
  const [programDescription, setProgramDescription] = useState("");
  const [hasBeenFirstUpdated, setHasBeenFirstUpdated] = useState(false);

  const programInfo = useFetchDataV2<any[]>(programInfoEndpoint, {
    browserCache: true,
    browserCacheKey: `programInfo-${id ?? ""}`,
    browserCacheNamespace: "programs",
    browserCacheTtlMs: PROGRAM_QUERY_GC_TIME,
    cacheId: id ? programQueryKeys.programInfo(id) : ["programInfo", ""],
    staleTime: PROGRAM_QUERY_STALE_TIMES.programsInfo,
    gcTime: PROGRAM_QUERY_GC_TIME,
  });
  const programs = useFetchDataV2<any[]>(programsEndpoint, {
    browserCache: true,
    browserCacheKey: "programsInfo",
    browserCacheNamespace: "programs",
    browserCacheTtlMs: PROGRAM_QUERY_GC_TIME,
    cacheId: programQueryKeys.programsInfo,
    staleTime: PROGRAM_QUERY_STALE_TIMES.programsInfo,
    gcTime: PROGRAM_QUERY_GC_TIME,
  });
  const programsOwners = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/programs`,
    "programsOwners",
    1000 * 60 * 60,
  );
  const shouldLoadRightPanel = useDeferredActivation(80);
  const shouldLoadCountries = useDeferredActivation(180);

  // ✅ Countries API
  const countriesQuery = useFetchDataV2<any[]>(
    shouldLoadCountries ? countriesEndpoint : "",
    programQueryKeys.countries,
    PROGRAM_QUERY_STALE_TIMES.countries,
    PROGRAM_QUERY_GC_TIME,
  );

  const { mutateAsync: save } = useMutateData({
    method: "POST",
    id: `programsInfo`,
    onError: (err) => {
      console.error("Erreur save", err);
    },
  });

  const { t } = useTranslation("dashboard", {
    keyPrefix: "programs",
  });

  // ✅ Program (no early return before hooks below!)
  const cachedPrograms = queryClient.getQueryData<any[]>([
    programQueryKeys.programsInfo,
    programsEndpoint,
  ]);
  const cachedProgramInfo = id
    ? queryClient.getQueryData<any[]>(programQueryKeys.programInfo(id))
    : undefined;
  const programInfoData = Array.isArray(programInfo?.data)
    ? programInfo.data
    : Array.isArray(cachedProgramInfo)
      ? cachedProgramInfo
      : [];
  const singleProgram = programInfoData.find((item) => item?.id == id);
  const programsData = Array.isArray(programs?.data)
    ? programs.data
    : Array.isArray(cachedPrograms)
      ? cachedPrograms
      : [];
  const program = useMemo(() => {
    return singleProgram ?? programsData.find((item) => item.id == id);
  }, [programsData, singleProgram, id]);
  const currentProgramOwnerCarrierId = useMemo(() => {
    const ownerProgram = programsOwners?.data?.find((item) => item.id == id);
    return ownerProgram?.carrier?.id ?? program?.carrier?.id ?? null;
  }, [programsOwners?.data, program?.carrier?.id, id]);
  const isDirectProgramOwner =
    currentProgramOwnerCarrierId != null &&
    user?.id != null &&
    Number(currentProgramOwnerCarrierId) === Number(user.id);
  const hidePostActions = useHidePostActions(currentProgramOwnerCarrierId);
  const { actsAsSubcontractorForScope, canManageScope } =
    useScopedDashboardAccess(currentProgramOwnerCarrierId);
  const canManageCurrentProgram =
    isDirectProgramOwner || (canManageScope && !hidePostActions);
  // =========================
  // ✅ FILTERS
  // =========================
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [launchFrom, setLaunchFrom] = useState<string>("");
  const [launchTo, setLaunchTo] = useState<string>("");

  const [countryId, setCountryId] = useState<number | null>(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  const [fundSearcher, setFundSearcher] = useState<FundSearcherFilter>("all");
  const [financing, setFinancing] = useState<FinancingFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");

  const countriesList = useMemo(() => {
    return Array.isArray(countriesQuery?.data) ? countriesQuery.data : [];
  }, [countriesQuery?.data]);

  const selectedCountry = useMemo(() => {
    if (!countryId) return null;
    return countriesList.find((c: any) => Number(c.id) === Number(countryId));
  }, [countriesList, countryId]);

  const visibleCountries = useMemo(() => {
    const q = (countrySearch || "").trim().toLowerCase();
    if (!q) return countriesList;
    return countriesList.filter((c: any) =>
      String(c?.name || "")
        .toLowerCase()
        .includes(q),
    );
  }, [countriesList, countrySearch]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      launchFrom ||
      launchTo ||
      countryId ||
      fundSearcher !== "all" ||
      financing !== "all" ||
      status !== "all",
    );
  }, [launchFrom, launchTo, countryId, fundSearcher, financing, status]);

  const computeFundingState = (p: any): FinancingFilter => {
    const df = p?.desiredFunding;
    const of = p?.obtainedFunding;

    const desiredArr = Array.isArray(df) ? df : [];
    const obtainedArr = Array.isArray(of) ? of : [];

    const isFull =
      df === null ||
      df === undefined ||
      desiredArr.length === 0 ||
      Number(p?.soldStatus) === 2;

    if (isFull) return "full";

    const isPartial =
      desiredArr.length > 0 &&
      obtainedArr.length > 0 &&
      Number(p?.soldStatus) !== 2;

    if (isPartial) return "partial";

    const isNone = desiredArr.length > 0 && Number(p?.soldStatus) !== 2;

    return isNone ? "none" : "all";
  };

  const matchProject = (p: any) => {
    // 1) launchDate interval
    if (launchFrom || launchTo) {
      const ld = p?.launchDate ? new Date(p.launchDate) : null;
      if (!ld || Number.isNaN(ld.getTime())) return false;

      if (launchFrom) {
        const fromDate = new Date(`${launchFrom}T00:00:00`);
        if (ld < fromDate) return false;
      }

      if (launchTo) {
        const toDate = new Date(`${launchTo}T23:59:59`);
        if (ld > toDate) return false;
      }
    }

    // 2) country
    if (countryId) {
      if (Number(p?.country?.id) !== Number(countryId)) return false;
    }

    // 3) fund searcher
    if (fundSearcher === "me") {
      if (Number(p?.searchFinancingInternal) !== 1) return false;
    }
    if (fundSearcher === "both") {
      if (Number(p?.searchFinancingInternal) === 1) return false;
    }

    // 4) financing
    if (financing !== "all") {
      const state = computeFundingState(p);
      if (state !== financing) return false;
    }

    // 5) status
    if (status !== "all") {
      if (Number(p?.status) !== Number(status)) return false;
    }

    return true;
  };

  const allProjects = useMemo(() => {
    return Array.isArray(program?.projects) ? program.projects : [];
  }, [program?.projects]);

  const filteredProjects = useMemo(() => {
    if (!hasActiveFilters) return allProjects;
    return allProjects.filter(matchProject);
  }, [
    allProjects,
    hasActiveFilters,
    launchFrom,
    launchTo,
    countryId,
    fundSearcher,
    financing,
    status,
  ]);

  const filterSummary = useMemo(() => {
    const parts: string[] = [];

    if (launchFrom || launchTo)
      parts.push(`Lancement: ${launchFrom || "…"} → ${launchTo || "…"}`);
    if (countryId) parts.push(`Pays: ${selectedCountry?.name || "…"}`);
    if (fundSearcher !== "all") {
      parts.push(
        `Chercheur: ${fundSearcher === "me" ? "Moi-même" : "Moi-même et Verdeo"
        }`,
      );
    }
    if (financing !== "all") {
      parts.push(
        `Financement: ${financing === "full"
          ? "Totalement financé"
          : financing === "partial"
            ? "Partiellement financé"
            : "Non financé"
        }`,
      );
    }
    if (status !== "all")
      parts.push(`Statut: ${STATUS_LABELS[Number(status)]}`);

    return parts.length ? parts.join(" • ") : "Aucun filtre";
  }, [
    launchFrom,
    launchTo,
    countryId,
    selectedCountry,
    fundSearcher,
    financing,
    status,
  ]);

  const handleResetFilters = () => {
    setLaunchFrom("");
    setLaunchTo("");
    setCountryId(null);
    setCountrySearch("");
    setFundSearcher("all");
    setFinancing("all");
    setStatus("all");
  };

  // =========================
  // ✅ Existing logic
  // =========================
  useEffect(() => {
    if (program && hasBeenFirstUpdated === false) {
      setProgramDescription(program?.description);
      setHasBeenFirstUpdated(true);
    }
  }, [program, hasBeenFirstUpdated]);

  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">
            {t("Bienvenue sur la fiche de programme !")}
          </h2>
          <p>
            {t(
              "Ici, vous pouvez voir un aperçu global des statistiques et des projets en cours.",
            )}
          </p>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#add-project",
      content: (
        <div className="text-center">
          <p>{t("Cliquez ici pour ajouter un nouveau projet.")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#program-title",
      content: (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">
            {t("Voici le programme en cours")}
          </h2>
          <p>{t("Vous pouvez consulter ses détails ci-dessous.")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#projects-lists",
      content: (
        <div className="text-center">
          <p>{t("Chaque carte représente un projet dans votre programme.")}</p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: ".edit-selection-btn",
      content: (
        <div className="text-center">
          <p>{t("Modifiez les projets sélectionnés avec ce bouton.")}</p>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#data-reporting",
      title: t("Vue géographique des projets"),
      content: (
        <div className="text-center">
          <p>{t("Cliquez ici pour voir vos projets sur une carte.")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true,
    },
  ];

  const [run, setRun] = useState(false);

  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem("hasSeenTutorialProgram");
    if (
      (!hasSeenTutorial && !programInfo.loading) ||
      (user?.email === "demo@verdeo.fr" && !programInfo.loading)
    ) {
      setRun(true);
    }
  }, [programInfo.loading, user?.email]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    autoScrollJoyrideStep(data);
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === "skip") {
      setRun(false);
      localStorage.setItem("hasSeenTutorialProgram", "true");
    }
  };

  const handleSave = async () => {
    const body = { name: programName };
    const previousPrograms = queryClient.getQueriesData({
      queryKey: [programQueryKeys.programsInfo],
    });

    updateProgramInProgramsInfoCache(queryClient, id, body);
    try {
      const response = await save({
        endpoint: `${import.meta.env.VITE_IP_API}/api/program/edit/${id}`,
        body,
      });
      if (response) toast({ title: "✅ Modification enregistrée !" });
    } catch (error) {
      previousPrograms.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      throw error;
    }
  };

  const handleEditClick = () => {
    setIsEditingDescription(!isEditingDescription);
  };

  const handleChangeDescription = (e) => {
    setProgramDescription(e.target.value);
  };

  const handleSaveDescription = async () => {
    const body = { description: programDescription };
    const previousPrograms = queryClient.getQueriesData({
      queryKey: [programQueryKeys.programsInfo],
    });

    updateProgramInProgramsInfoCache(queryClient, id, body);
    try {
      const response = await save({
        endpoint: `${import.meta.env.VITE_IP_API}/api/program/edit/${id}`,
        body,
      });
      if (response) {
        setIsEditingDescription(false);
        toast({ title: "✅ Modification enregistrée !" });
      }
    } catch (error) {
      previousPrograms.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      throw error;
    }
  };

  const handleSubmit = async (
    e: FormEvent<HTMLFormElement>,
    projectsTemp: any,
    projectsTempBdd: any,
  ) => {
    e.preventDefault();
    window.scrollTo(0, 0);

    const projectsArray: any[] = [];
    projectsTemp.map((project: any) => {
      if (project.type)
        projectsArray.push({ name: project.name, type: project.type });
    });

    const projectsArrayBdd: any[] = [];
    projectsTempBdd?.map((project: any) => {
      if (project.type)
        projectsArrayBdd.push({ name: project.name, type: project.type });
    });

    const selectedProject = projectsArrayBdd[projectsArrayBdd.length - 1];
    const previousProgramQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    });
    const previousProgramInfoQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: programQueryKeys.programInfo(id ?? ""),
    });
    const restorePreviousProjectCaches = () => {
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    };
    const existingProjectIds = new Set(
      (Array.isArray(program?.projects) ? program.projects : [])
        .map((project: any) => Number(project?.id ?? 0))
        .filter((projectId: number) => Number.isFinite(projectId) && projectId > 0),
    );
    const tempProjectId = -Date.now();
    if (selectedProject) {
      upsertProgramProjectInProgramsInfoCache(queryClient, id, {
        id: tempProjectId,
        name: selectedProject.name,
        type: selectedProject.type,
        status: 1,
        soldStatus: 0,
        __optimistic: true,
      });
      setOpenModal({ name: "project", value: false, programId: Number(id) });
    }

    let programSaved = null;
    try {
      programSaved = await updateProgramInDB(
        { projects: [...projectsArrayBdd] },
        Number(id),
      );
    } catch (error) {
      if (selectedProject) {
        restorePreviousProjectCaches();
        setOpenModal({ name: "project", value: true, programId: Number(id) });
        toast({
          className: "text-red-500",
          title: t("Une erreur est survenue lors de la création du projet."),
          duration: 2500,
        });
      }
      return;
    }

    const savedProject =
      programSaved?.projects?.find(
        (project: any) => !existingProjectIds.has(Number(project?.id ?? 0)),
      ) ??
      programSaved?.projects?.[programSaved.projects.length - 1] ??
      null;
    if (selectedProject && !savedProject) {
      restorePreviousProjectCaches();
      setOpenModal({ name: "project", value: true, programId: Number(id) });
      toast({
        className: "text-red-500",
        title: t("Une erreur est survenue lors de la création du projet."),
        duration: 2500,
      });
      return;
    }

    toast({ title: t("Vos informations sont bien prises en compte.") });
    if (programSaved?.projects) {
      if (savedProject && selectedProject) {
        const projectForCache = {
          ...savedProject,
          program: savedProject.program ?? {
            id: programSaved.id ?? Number(id),
            name: programSaved.name ?? program?.name,
          },
        };
        upsertProgramProjectInProgramsInfoCache(
          queryClient,
          id,
          { ...projectForCache, __optimistic: false },
          tempProjectId,
        );
      }
      updateProgramInProgramsInfoCache(queryClient, id, {
        projects: programSaved.projects,
      });
    }
    queryClient.invalidateQueries({ queryKey: ["programsInfoTree"] });
    queryClient.invalidateQueries({ queryKey: [programQueryKeys.programsInfo] });
    if (savedProject?.id) {
      navigate(`/dashboard/project/${savedProject.id}`);
    }
  };

  const handleDeleteProjectOptimistic = async (
    programId: number,
    projectId: number,
    deleteRequest: () => Promise<any>,
  ) => {
    const previousProgramQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    });
    const previousProgramInfoQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: programQueryKeys.programInfo(programId),
    });

    removeProgramProjectFromProgramsInfoCache(queryClient, programId, projectId);

    const res = await deleteRequest();
    const hasFailed =
      res === null ||
      (typeof res === "object" && "success" in res && !res.success);

    if (hasFailed) {
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      return res;
    }

    void queryClient.invalidateQueries({ queryKey: ["programsInfoTree"] });
    void queryClient.invalidateQueries({ queryKey: ["AapProject"] });

    return typeof res === "object" && res !== null ? res : { success: true };
  };

  const extractDuplicatedProject = (res: any) => {
    if (!res || typeof res !== "object") return null;
    if (res.id) return res;

    const candidates = [
      res.project,
      res.data,
      res.duplicatedProject,
      res.projectDuplicated,
      res.newProject,
      res.result,
    ];

    for (const candidate of candidates) {
      if (candidate?.id) return candidate;
      if (Array.isArray(candidate)) {
        const project = candidate.find((item: any) => item?.id);
        if (project) return project;
      }
    }

    return Array.isArray(res.projects)
      ? res.projects.find((candidate: any) => candidate?.id) ?? null
      : null;
  };

  const handleDuplicateProjectOptimistic = async (
    programId: number,
    project: any,
    duplicateRequest: () => Promise<any>,
  ) => {
    const previousProgramQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    });
    const previousProgramInfoQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: programQueryKeys.programInfo(programId),
    });
    const tempProjectId = -Date.now();
    const optimisticProject = {
      ...project,
      id: tempProjectId,
      name: `${project?.name ?? project?.title ?? t("Projet")} - copie`,
      status: project?.status ?? 1,
      soldStatus: project?.soldStatus ?? 0,
      program: project?.program ?? {
        id: programId,
        name: program?.name,
      },
      __optimistic: true,
    };

    upsertProgramProjectInProgramsInfoCache(
      queryClient,
      programId,
      optimisticProject,
    );

    let res = null;
    try {
      res = await duplicateRequest();
    } catch (error) {
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      return { success: false, error };
    }
    const hasFailed =
      res === null ||
      (typeof res === "object" && "success" in res && !res.success);

    if (hasFailed) {
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      return res;
    }

    const duplicatedProject = extractDuplicatedProject(res);
    if (duplicatedProject?.id) {
      upsertProgramProjectInProgramsInfoCache(
        queryClient,
        programId,
        {
          ...duplicatedProject,
          program: duplicatedProject.program ?? {
            id: programId,
            name: program?.name,
          },
          __optimistic: false,
        },
        tempProjectId,
      );
    }

    void queryClient.invalidateQueries({ queryKey: ["programsInfoTree"] });
    void queryClient.invalidateQueries({ queryKey: ["AapProject"] });

    return typeof res === "object" && res !== null ? res : { success: true };
  };

  const isProgramLoading =
    programInfo.loading || (programs.loading && programsData.length === 0);
  if (!program && isProgramLoading) return <LoadingSpinner />;
  if (programInfo.error && programs.error && programsData.length === 0) {
    new Error(t("Une erreur est survenue: ") + programInfo.error);
    return null;
  }
  if (!program) {
    const isSubcontractor = actsAsSubcontractorForScope;
    return (
      <div className="rounded-2xl border border-[#e1e8dc] bg-white p-6">
        <h1 className="text-lg font-semibold text-[#2f3f2d]">
          Programme inaccessible
        </h1>
        <p className="mt-2 text-sm text-[#5f6d5c]">
          {isSubcontractor
            ? "Vous n'avez plus acces a aucun projet dans ce programme. Veuillez contacter votre porteur de projet."
            : "Ce programme est introuvable ou vous n'avez pas les droits d'acces."}
        </p>
        <div className="mt-4">
          <Button type="button" variant="outline" onClick={() => (window.location.href = "/dashboard/programs")}>
            Retour aux programmes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Joyride
        steps={steps}
        run={run}
        callback={handleJoyrideCallback}
        continuous={false}
        scrollToFirstStep={false}
        disableScrolling={true}
        showProgress={true}
        showSkipButton={true}
        spotlightClicks={false}
        locale={{
          back: t("Précédent"),
          close: t("Suivant"),
          last: t("Terminer"),
          next: t("Suivant"),
          skip: t("Passer"),
        }}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "  #383937",
            overlayColor: "rgba(255, 255, 255, 0.8)",
            spotlightShadow: "0 0 0 rgba(0, 0, 0, 0)",
            textColor: "#fff",
            zIndex: 1000,
          },
          buttonBack: {
            backgroundColor: "#669523",
            color: "white",
            borderRadius: "20px",
            marginRight: "20px",
            width: "100px",
            fontSize: "15px",
          },
          buttonNext: {
            backgroundColor: "#669523",
            color: "white",
            borderRadius: "20px",
            width: "80px",
            fontSize: "15px",
          },
        }}
      />

      <ReactBreadcrumb
        items={[
          { href: "/dashboard/programs", name: t("Mes programmes") },
          { href: ``, name: program?.name || "..." },
        ]}
      />

      <h1
        className={`mt-4 mb-4 text-[24px] font-medium ${isEditing ? "inline-flex items-start w-full" : "inline-block"
          }`}
        id="program-title"
      >
        {isEditing ? (
          <Input
            value={programName}
            onChange={(e) => setProgramName(e.target.value)}
            className="inline-block min-w-96 "
          />
        ) : (
          `${t("Programme")} ${program?.name || ""}`
        )}

        {program?.name &&
          canManageCurrentProgram && (
            <button
              className={`ml-2 inline-flex items-center justify-center border rounded-md ${isEditing
                ? "size-10 bg-verdeogreen text-white border-none"
                : "size-8"
                }`}
              onClick={() => {
                if (isEditing) {
                  handleSave();
                  setIsEditing(false);
                } else {
                  setProgramName(program?.name || "");
                  setIsEditing(true);
                }
              }}
              title={isEditing ? "Valider" : "Modifier"}
            >
              {isEditing ? <Check /> : <Edit3 />}
            </button>
          )}
      </h1>

      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
        {isEditingDescription ? (
          <Textarea
            value={programDescription}
            onChange={handleChangeDescription}
            rows={4}
            className="inline-block"
          />
        ) : (
          <p style={{ whiteSpace: "pre-line" }}>
            {programDescription || "Aucune description disponible."}
          </p>
        )}

        {canManageCurrentProgram && (
          <button
            onClick={() => {
              if (isEditingDescription) {
                handleSaveDescription();
              } else {
                setProgramDescription(program?.description || "");
                handleEditClick();
              }
            }}
            className={`ml-2 inline-flex items-center justify-center border rounded-md ${isEditingDescription
              ? "size-10 bg-verdeogreen text-white border-none"
              : "size-6"
              }`}
          >
            {isEditingDescription ? <Check /> : <Edit3 />}
          </button>
        )}
      </div>

      <div className="flex flex-wrap mt-3">
        <div className="basis-1/2 pr-4 min-h-40 relative" id="projects-lists">
          <div className="absolute right-0 top-0 w-1 h-40 "></div>

          {/* ✅ Filters above the list */}
          <div className="mb-3 rounded-2xl border bg-background p-3">
            <button
              type="button"
              className="flex w-full items-start justify-between gap-3"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex size-9 items-center justify-center rounded-xl bg-muted">
                  <Filter className="size-5 text-muted-foreground" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-foreground">
                    Filtres projets
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {filterSummary}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {hasActiveFilters
                      ? `${filteredProjects.length} / ${allProjects.length
                      } projet${allProjects.length > 1 ? "s" : ""}`
                      : `${allProjects.length} projet${allProjects.length > 1 ? "s" : ""
                      }`}
                  </div>
                </div>
              </div>

              <div className="shrink-0 inline-flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetFilters();
                  }}
                  title="Réinitialiser"
                >
                  <X className="size-4" />
                </Button>
                {filtersOpen ? (
                  <ChevronUp className="size-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="size-5 text-muted-foreground" />
                )}
              </div>
            </button>

            {filtersOpen && (
              <div className="mt-4 grid grid-cols-1 gap-3">
                {/* Date launch */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Date de lancement (début)
                    </div>
                    <Input
                      type="date"
                      value={launchFrom}
                      onChange={(e) => setLaunchFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Date de lancement (fin)
                    </div>
                    <Input
                      type="date"
                      value={launchTo}
                      onChange={(e) => setLaunchTo(e.target.value)}
                    />
                  </div>
                </div>

                {/* Country searchable */}
                <div>
                  <div className="mb-1 text-xs font-semibold text-foreground">
                    Pays
                  </div>
                  <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between"
                      >
                        <span className="truncate">
                          {selectedCountry?.name || "Tous les pays"}
                        </span>
                        <ChevronDown className="ml-2 size-4 opacity-60" />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent className="w-[320px] p-2" align="start">
                      <div className="mb-2">
                        <Input
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          placeholder="Rechercher un pays…"
                        />
                      </div>

                      <div className="max-h-60 overflow-auto rounded-md border">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            setCountryId(null);
                            setCountryOpen(false);
                          }}
                        >
                          Tous les pays
                        </button>

                        {visibleCountries.map((c: any) => (
                          <button
                            key={c.id}
                            type="button"
                            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setCountryId(Number(c.id));
                              setCountryOpen(false);
                            }}
                          >
                            <span className="truncate">{c.name}</span>
                            {Number(countryId) === Number(c.id) ? (
                              <Check className="size-4 opacity-70" />
                            ) : null}
                          </button>
                        ))}

                        {!visibleCountries.length && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            Aucun résultat
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Fund searcher & Financing */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Chercheur de fond
                    </div>
                    <select
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                      value={fundSearcher}
                      onChange={(e) =>
                        setFundSearcher(e.target.value as FundSearcherFilter)
                      }
                    >
                      <option value="all">Tous</option>
                      <option value="me">Moi-même</option>
                      <option value="both">Moi-même et Verdeo</option>
                    </select>
                  </div>

                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Financement
                    </div>
                    <select
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                      value={financing}
                      onChange={(e) =>
                        setFinancing(e.target.value as FinancingFilter)
                      }
                    >
                      <option value="all">Tous</option>
                      <option value="full">Totalement financé</option>
                      <option value="partial">Partiellement financé</option>
                      <option value="none">Non financé</option>
                    </select>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <div className="mb-1 text-xs font-semibold text-foreground">
                    Statut
                  </div>
                  <select
                    className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                    value={String(status)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStatus(
                        v === "all" ? "all" : (Number(v) as StatusFilter),
                      );
                    }}
                  >
                    <option value="all">Tous</option>
                    <option value="0">Non commencé</option>
                    <option value="1">En cours</option>
                    <option value="2">En attente de validation</option>
                    <option value="3">Validé</option>
                    <option value="4">Rejeté</option>
                    <option value="5">Cloturé</option>
                    <option value="6">En attente de votre validation</option>
                  </select>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResetFilters}
                  >
                    Réinitialiser
                  </Button>
                </div>
              </div>
            )}
          </div>

          {programs.loading && programsData.length === 0 && !program && (
            <CardSkeleton />
          )}

          {program ? (
            <>
              {hasActiveFilters && filteredProjects.length === 0 ? (
                <div className="mb-3 rounded-2xl border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                  Aucun projet ne correspond aux filtres.
                </div>
              ) : null}
              {!programs.loading &&
                allProjects.length === 0 &&
                !hasActiveFilters && (
                  <div className="mb-3 text-sm text-muted-foreground">
                    {t(
                      'Il n\'y a pas encore de projets dans votre programme. Veuillez en ajouter un en cliquant sur le bouton "Ajouter".',
                    )}
                  </div>
                )}
                <ProjectList
                  projects={hasActiveFilters ? filteredProjects : allProjects}
                  programId={program?.id}
                  programsParam={
                    Array.isArray(programsOwners?.data)
                      ? programsOwners.data
                      : [program]
                  }
                  onDeleteProject={handleDeleteProjectOptimistic}
                  onDuplicateProject={handleDuplicateProjectOptimistic}
                />
            </>
          ) : (
            null
          )}
        </div>

        <div className="basis-1/2 pl-4">
          {shouldLoadRightPanel ? (
            <Suspense fallback={program ? <TabSkeleton /> : null}>
              <LazyTab prog={program} />
            </Suspense>
          ) : (
            <TabSkeleton />
          )}
        </div>
      </div>

      {modals["project"] && <AddProject onHandleSubmit={handleSubmit} />}
    </div>
  );
};

export default DashboardProgram;

export const TabSkeleton = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "programs",
  });
  return (
    <>
      <Tabs className="w-full" defaultValue="objectif">
        <TabsList className="mb-2 grid w-full grid-cols-2 bg-background">
          <TabsTrigger
            value="objectif"
            className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none"
          >
            {t("Objectifs du programme")}
          </TabsTrigger>
          <TabsTrigger
            id="data-reporting"
            value="zone"
            className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none"
          >
            {t("Zone du programme")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="objectif" className="mb-4">
          <div className="mt-4 flex justify-end mb-4">
            <Button
              variant={"outline"}
              className="edit-selection-btn rounded-e-none border-r-0"
              disabled={true}
            >
              <Filter className="mr-3 size-5 text-muted-foreground" />
              {t("Filtrer les projets")}
            </Button>
            <Button
              variant={"outline"}
              className="edit-selection-btn rounded-s-none"
              disabled={true}
            >
              <Eye className="mr-3 size-5 text-muted-foreground" />
              {t("Afficher les indicateurs")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                className="rounded-[15px] w-[165px] h-[100px] bg-[#ecedee]"
              />
            ))}
          </div>
          <div className="grid grid-cols-1 mt-4">
            <ChartSkeleton />
          </div>
        </TabsContent>
        <TabsContent value="zone" className="mb-4">
          <MapSkeleton />
        </TabsContent>
      </Tabs>
    </>
  );
};
